import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { WarningIcon } from '@phosphor-icons/react/dist/csr/Warning';

import { Button } from '../../design-system/primitives/Button/Button';
import { Card } from '../../design-system/composites/Card/Card';
import { ClientDataTable } from '../../design-system/composites/DataTable/ClientDataTable';
import type { Column } from '../../design-system/composites/DataTable/DataTable';
import { Combobox } from '../../design-system/primitives/Combobox/Combobox';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import { ApiError } from '../../lib/api/client';
import {
  escalationRoutesApi,
  type EscalationRouteInput,
  type EscalationRouteLevel,
  type EscalationRouteRow,
} from '../../lib/api/escalations';
import { dispensariesApi, type Dispensary } from '../../lib/api/reference';
import { mapDetails } from '../reference/referenceFormHelpers';
import { RolePicker } from '../shared/RolePicker';
import { StaffPicker } from '../shared/StaffPicker';
import styles from './RouteSettings.module.css';
import { referenceName } from '../../lib/format/referenceName';

/**
 * The escalation-routes configuration panel (M-10, Task 5, spec E-5) — a
 * PANEL on the escalations screen, gated on `escalation.manage`, never a
 * second nav entry (spec §3: "`/escalations/routes` is not a separate nav
 * entry").
 *
 * ## `PUT` replaces the whole active set for ONE dispensary, never edits a row
 *
 * `escalationRoutesApi.update()` mirrors `EscalationRouteController::update()`'s
 * own contract exactly — see that class's own docblock: every currently
 * active row for the target `dispensary_id` (or the global fallback,
 * `null`) is deactivated and one fresh row is inserted per submitted route.
 * This panel therefore never sends a partial edit of one level; choosing a
 * dispensary loads its three levels' current assignees, and Save always
 * replaces all three at once for that target.
 *
 * ## Role/user assignment is picked by name, never typed as a UUID
 *
 * `rolesApi.list()`/`usersApi.list()` need `user.role.assign`/`user.view`,
 * which `escalation.manage` does not imply — so this panel once took raw
 * UUIDs copied off the Users screen, which nobody could fill in. The
 * assignee cell is now a `RolePicker` or `StaffPicker`, backed by
 * `GET /lookups/roles` and `GET /lookups/staff`, both of which
 * `escalation.manage` opens (the API's `LookupPolicy`). The panel's
 * permission surface is still exactly `escalation.manage`, and the wire is
 * unchanged: `role_id`/`user_id` carry the picked uuid. A saved route's
 * `role_name`/`user_name` names the prefilled choice before the pickers have
 * loaded anything.
 *
 * ## Editing a route never reassigns an escalation already in flight
 *
 * `ChainRouter` is consulted only at the MOMENT of a transition
 * (`EscalationRouteController`'s own class docblock) — this panel's Save
 * has no effect on any escalation already sitting at whatever level it just
 * reconfigured, and nothing here tries to predict or display that effect.
 *
 * ## The server's per-row, per-field errors are surfaced against the right
 * row (whole-branch review, I-2)
 *
 * `UpdateEscalationRoutesRequest::validateRowShape()`/`validateAssigneeShape()`
 * key their `422`s `routes.{index}.role_id`, `routes.{index}.user_id`,
 * `routes.{index}.level` — `{index}` is the position in the array THIS
 * PANEL SENT, which is `filteredLevels` below, not `LEVELS`: a level whose
 * `assigneeType` is `'none'` is dropped from the payload before it reaches
 * the wire, so index 0 of the request is not necessarily level 1. When the
 * assignee was a free-text UUID, a mistyped id failing `Rule::exists` was
 * the likeliest single failure; with pickers it is rarer (a role or account
 * removed between load and Save) but still real — and the one this panel
 * once refused to show, collapsing every `422` into one generic sentence with no row
 * attached. `mapDetails` (the same helper `EscalationFormDialog`/
 * `EscalationActionDialog` use) turns `error.details` into field -> first
 * message; the `routes.` prefix is then parsed back to the LEVEL that sent
 * that index, and the message renders against that level's own assignee
 * picker. Only when no key matches that shape (a validation failure this
 * panel does not know how to place, or a non-validation error) does the
 * generic `saveFailed` sentence still appear — same fallback, never the
 * only path.
 */

export interface RouteSettingsProps {
  canManage: boolean;
  /** One line under the card head saying what the routing governs. */
  intro?: ReactNode;
}

type LoadStatus = 'loading' | 'ready' | 'error';

type AssigneeType = 'none' | 'role' | 'user';

interface LevelDraft {
  assigneeType: AssigneeType;
  assigneeId: string;
  /** The saved route's `role_name`/`user_name`, so a prefilled picker reads as a name before it loads. */
  assigneeName: string;
  category: string;
}

const BLANK_LEVEL: LevelDraft = { assigneeType: 'none', assigneeId: '', assigneeName: '', category: '' };

const LEVELS: readonly EscalationRouteLevel[] = [1, 2, 3];

/** The Combobox's own synthetic value for "the global fallback" — `dispensary_id: null` on the wire. Never a real dispensary uuid, which is always a v4 string. */
const GLOBAL_VALUE = '__global__';

export function RouteSettings({ canManage, intro }: RouteSettingsProps) {
  const { t, i18n } = useTranslation();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [routes, setRoutes] = useState<readonly EscalationRouteRow[]>([]);
  const [dispensaries, setDispensaries] = useState<readonly Dispensary[]>([]);
  const [selectedDispensary, setSelectedDispensary] = useState<string>(GLOBAL_VALUE);
  const [levels, setLevels] = useState<Record<EscalationRouteLevel, LevelDraft>>({
    1: BLANK_LEVEL,
    2: BLANK_LEVEL,
    3: BLANK_LEVEL,
  });
  const idPrefix = useId();
  const [saving, setSaving] = useState(false);
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  const [levelErrors, setLevelErrors] = useState<Partial<Record<EscalationRouteLevel, string>>>({});

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const [routeList, dispensaryList] = await Promise.all([
        escalationRoutesApi.list(),
        dispensariesApi.list(false),
      ]);
      setRoutes(routeList);
      setDispensaries(dispensaryList);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!canManage) return;
    void load();
  }, [canManage, load]);

  // Prefill the three level rows from whatever is currently active for the
  // selected target — never editing a row in place server-side (this
  // file's own docblock), but the FORM still starts from what is live so
  // Save does not silently clear levels the caller never meant to touch.
  useEffect(() => {
    const targetUuid = selectedDispensary === GLOBAL_VALUE ? null : selectedDispensary;

    const next: Record<EscalationRouteLevel, LevelDraft> = { 1: BLANK_LEVEL, 2: BLANK_LEVEL, 3: BLANK_LEVEL };
    for (const route of routes) {
      if (route.dispensary_id !== targetUuid || !route.is_active) continue;
      next[route.level] = {
        assigneeType: route.role_id !== null ? 'role' : route.user_id !== null ? 'user' : 'none',
        assigneeId: route.role_id ?? route.user_id ?? '',
        assigneeName: route.role_name ?? route.user_name ?? '',
        category: route.category ?? '',
      };
    }
    setLevels(next);
  }, [routes, selectedDispensary]);

  function updateLevel(level: EscalationRouteLevel, patch: Partial<LevelDraft>): void {
    setLevels((current) => ({ ...current, [level]: { ...current[level], ...patch } }));
    // A fresh edit invalidates whatever the server last said about this
    // row's assignee — cleared here rather than left to display against a
    // value the caller has since changed.
    setLevelErrors((current) => {
      if (current[level] === undefined) return current;
      const next = { ...current };
      delete next[level];
      return next;
    });
  }

  async function handleSave(): Promise<void> {
    if (saving) return;
    setSaving(true);
    setSaveFailure(null);
    setLevelErrors({});

    // `filteredLevels[i]` is the LEVEL that produced `input[i]` — the array
    // actually sent over the wire, and therefore the array the server's
    // `routes.{index}.*` error keys index into. A level whose
    // `assigneeType` is `'none'` is dropped here, so `input`'s own indices
    // do not line up with `LEVELS`' (see this file's own docblock, I-2).
    const filteredLevels = LEVELS.filter(
      (level) => levels[level].assigneeType !== 'none' && levels[level].assigneeId.trim() !== '',
    );
    const input: readonly EscalationRouteInput[] = filteredLevels.map((level) => {
      const draft = levels[level];
      const category = draft.category.trim();
      return {
        level,
        role_id: draft.assigneeType === 'role' ? draft.assigneeId.trim() : null,
        user_id: draft.assigneeType === 'user' ? draft.assigneeId.trim() : null,
        category: category === '' ? null : category,
      };
    });

    try {
      await escalationRoutesApi.update({
        dispensary_id: selectedDispensary === GLOBAL_VALUE ? null : selectedDispensary,
        routes: input,
      });
      // Refetched rather than merged from the response — the response is
      // only the target dispensary's own active rows after the replace,
      // and this panel's read-only table below shows every dispensary's.
      await load();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);
      const perLevel: Partial<Record<EscalationRouteLevel, string>> = {};
      for (const [key, message] of Object.entries(mapped)) {
        const match = /^routes\.(\d+)\.(role_id|user_id|level)$/.exec(key);
        if (match === null) continue;
        const level = filteredLevels[Number(match[1])];
        if (level === undefined) continue;
        perLevel[level] = message;
      }

      if (Object.keys(perLevel).length > 0) {
        setLevelErrors(perLevel);
      } else {
        setSaveFailure(t('escalations.routes.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  }

  const dispensaryOptions = useMemo(
    () => [
      { value: GLOBAL_VALUE, label: t('escalations.routes.globalFallback') },
      ...dispensaries.map((d) => ({ value: d.uuid, label: referenceName(d, i18n.language) })),
    ],
    [dispensaries, t, i18n.language],
  );

  const dispensaryNameByUuid = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of dispensaries) map.set(d.uuid, referenceName(d, i18n.language));
    return map;
  }, [dispensaries, i18n.language]);

  /*
   * FLATTENED for the table, not passed through raw. `ClientDataTable`
   * searches and sorts the ROW OBJECT's own values, so the dispensary has to
   * arrive here already resolved from its uuid to its name — otherwise
   * typing "Aldawa" into the search box matches nothing while the column
   * plainly reads "Aldawa", and the sort orders by uuid.
   */
  const routeRows = useMemo(
    () =>
      routes.map((route) => ({
        id: route.uuid,
        dispensary:
          route.dispensary_id === null
            ? t('escalations.routes.globalFallback')
            : (dispensaryNameByUuid.get(route.dispensary_id) ?? route.dispensary_id),
        level: route.level,
        assignee: route.role_name ?? route.user_name ?? null,
        category: route.category ?? null,
        active: route.is_active
          ? t('escalations.routes.active')
          : t('escalations.routes.inactive'),
      })),
    [routes, dispensaryNameByUuid, t],
  );

  const routeColumns: readonly Column[] = useMemo(
    () => [
      { key: 'dispensary', label: t('escalations.routes.dispensaryLabel') },
      { key: 'level', label: t('escalations.level'), numeric: true },
      { key: 'assignee', label: t('escalations.assignedTo') },
      { key: 'category', label: t('escalations.routes.categoryLabel') },
      { key: 'active', label: t('escalations.routes.activeLabel') },
    ],
    [t],
  );

  const assigneeTypeOptions: readonly SelectOption[] = [
    { value: 'none', label: t('escalations.routes.assigneeNone') },
    { value: 'role', label: t('escalations.routes.assigneeRole') },
    { value: 'user', label: t('escalations.routes.assigneeUser') },
  ];

  if (!canManage) return null;

  return (
    <div className={styles['panel']}>
      <Card
        wash
        icon={<WarningIcon size={16} />}
        title={t('escalations.routes.title')}
        level="h2"
        data-testid="routeSettingsCard"
      >
        {intro === undefined ? null : <p className={styles['intro']}>{intro}</p>}

        {status === 'loading' ? <p role="status" className={styles['intro']}>{t('list.loading')}</p> : null}

        {status === 'error' ? (
          <div role="alert" className={styles['empty']}>
            <p>{t('escalations.routes.loadFailed')}</p>
            <Button
              variant="secondary"
              onClick={() => {
                void load();
              }}
              icon={<ArrowClockwiseIcon size={16} />}
            >
              {t('escalations.retry')}
            </Button>
          </div>
        ) : null}

        {status === 'ready' ? (
          <div className={styles['editor']}>
            {/* The dispensary being edited, and Save at the end of the same
                row — the command that commits the three levels below. */}
            <div className={styles['scopeRow']}>
              <Combobox
                label={t('escalations.routes.dispensaryLabel')}
                width="fill"
                options={dispensaryOptions}
                value={selectedDispensary}
                emptyMessage={t('escalations.routes.noDispensariesYet')}
                onChange={setSelectedDispensary}
              />

              <Button
                loading={saving}
                onClick={() => {
                  void handleSave();
                }}
                icon={<FloppyDiskIcon size={16} />}
              >
                {t('escalations.routes.save')}
              </Button>
            </div>

            {/*
              ONE grid for the three levels: each level's fieldset is a row
              whose columns are the grid's own (`subgrid`), so "Assign to",
              the assignee field and "Category" are the same width on every
              level, and the "Assign to" column is as wide as its longest
              option ("A specific user") instead of a fixed 130px that cut it
              off. A level with nobody assigned keeps an empty cell, so its
              Category does not slide left out of its column.
            */}
            <div className={styles['levels']}>
              {LEVELS.map((level) => (
                // A `div role="group"`, not a `<fieldset>`: a fieldset cannot
                // be a `subgrid` (Chromium lays its content out in an
                // anonymous box), and the subgrid is the point of this row.
                <div
                  key={level}
                  role="group"
                  aria-labelledby={`${idPrefix}-level-${level}`}
                  className={styles['levelRow']}
                >
                  <span className={styles['levelChip']} id={`${idPrefix}-level-${level}`}>
                    {t('escalations.routes.levelHeading', { level })}
                  </span>

                  <Select
                    label={t('escalations.routes.assigneeTypeLabel')}
                    width="fill"
                    options={assigneeTypeOptions}
                    value={levels[level].assigneeType}
                    onChange={(event) => {
                      const { value } = event.target;
                      updateLevel(level, { assigneeType: value as AssigneeType, assigneeId: '', assigneeName: '' });
                    }}
                  />

                  {levels[level].assigneeType === 'none' ? (
                    <div aria-hidden="true" />
                  ) : levels[level].assigneeType === 'role' ? (
                    <RolePicker
                      label={t('escalations.routes.roleLabel')}
                      width="fill"
                      value={levels[level].assigneeId === '' ? null : levels[level].assigneeId}
                      selectedLabel={levels[level].assigneeName || undefined}
                      error={levelErrors[level]}
                      onChange={(uuid) => {
                        updateLevel(level, { assigneeId: uuid });
                      }}
                    />
                  ) : (
                    <StaffPicker
                      label={t('escalations.routes.userLabel')}
                      width="fill"
                      value={levels[level].assigneeId === '' ? null : levels[level].assigneeId}
                      selectedLabel={levels[level].assigneeName || undefined}
                      error={levelErrors[level]}
                      onChange={(uuid) => {
                        updateLevel(level, { assigneeId: uuid });
                      }}
                    />
                  )}

                  <Input
                    label={t('escalations.routes.categoryLabel')}
                    width="fill"
                    value={levels[level].category}
                    onChange={(event) => {
                      const { value } = event.target;
                      updateLevel(level, { category: value });
                    }}
                  />
                </div>
              ))}
            </div>

            <div className={styles['saveFailure']} role="alert" aria-atomic="true">
              {saveFailure}
            </div>
          </div>
        ) : null}
      </Card>

      {status === 'ready' ? (
        <>
          {/*
            The hand-rolled `<table>` this replaced had no sort, no search, no
            empty state and no scroll region — and it is the one table on the
            screen that grows with the estate: one row per dispensary per
            level, so sixteen dispensaries is already forty-eight rows nobody
            can find anything in. Every route is loaded in one fetch (there is
            no cursor on this endpoint), so `ClientDataTable` is the honest
            fit; see its docblock for when it is not.
          */}
          <ClientDataTable
            caption={t('escalations.routes.tableCaption')}
            columns={routeColumns}
            rows={routeRows}
            status={routeRows.length === 0 ? 'empty' : 'ready'}
            empty={{ body: t('escalations.routes.emptyTable') }}
          />
        </>
      ) : null}
    </div>
  );
}
